import {useIsClientSide} from './hooks/useIsClientSide'

declare global {
  interface Window {
    ketch?: (action: string, ...args: any[]) => void
    semaphore?: any[]
  }
}

export const KetchConsentPurpose = {
  Analytics: 'analytics',
  TargetedAdvertising: 'targeted_advertising',
  EssentialServices: 'essential_services'
} as const

export type KetchConsentPurposeValue = (typeof KetchConsentPurpose)[keyof typeof KetchConsentPurpose]

const DECLINED_STORAGE_PREFIX = 'nb-consent-declined-'

/**
 * Why a server-rendered page left the Ketch boot out. `in-app`: an iOS or Android WebView, where no
 * consent prompt may appear. `handoff`: a signed-in account holder sent from the iOS app to check
 * out on the web; they accepted our terms in the app, and the web app they'd land on loads no Ketch.
 */
export type KetchSuppression = 'in-app' | 'handoff'

/** Set on `<body>` by the login document exactly when it leaves the Ketch boot out. */
export const ketchSuppressedAttribute = 'data-ketch-suppressed'

export function ketchSuppression(): KetchSuppression | null {
  if (typeof document === 'undefined') {
    return null
  }

  const value = document.body?.getAttribute(ketchSuppressedAttribute)
  return value === 'in-app' || value === 'handoff' ? value : null
}

/** For render: false on the server and first client pass, so a hydrated page matches its HTML. */
export function useKetchSuppressed(): boolean {
  return useIsClientSide() && ketchSuppression() !== null
}

/** The shape Ketch hands its `consent` listeners. Only the purposes map is ours to read. */
type KetchConsent = {purposes?: Partial<Record<KetchConsentPurposeValue, boolean>>}

/**
 * Records an explicit decline so surfaces that don't load Ketch can honor it.
 *
 * The web app (notability.com/app) deliberately doesn't load the Ketch tag, so it has no
 * consent object of its own. It's same-origin with login and marketing, which do, so those
 * surfaces persist the decision here and the app reads it back via `consentDeclined`.
 *
 * Writes on every reported state rather than on transitions: a user who was already declined
 * when the listener attached never produces a transition, and they're the whole point.
 *
 * Not dead code, though it looks it from the US. As of 2026-07-31 our Ketch config reports
 * `jurisdictionCode: default` with every purpose on `legalBasisCode: disclosure`, which is
 * notice-only: the purposes render "Always Active", the toggles are disabled, and Ketch never
 * reports a purpose as false. Jurisdictions that resolve to a consent basis (EU) are where a
 * decline actually arrives, and they're who this exists for. Don't delete it on the strength of
 * "I couldn't make it fire."
 */
function persistDeclinedPurposes(consent: KetchConsent | undefined): void {
  for (const purpose of [KetchConsentPurpose.Analytics, KetchConsentPurpose.TargetedAdvertising]) {
    // Only an explicit `false` is a decline. A missing purpose means Ketch has no opinion.
    const declined = consent?.purposes?.[purpose] === false
    try {
      if (declined) {
        window.localStorage.setItem(DECLINED_STORAGE_PREFIX + purpose, '1')
      } else if (consent?.purposes?.[purpose] === true) {
        window.localStorage.removeItem(DECLINED_STORAGE_PREFIX + purpose)
      }
    } catch {
      // Storage can be unavailable (private browsing, blocked cookies). Nothing to do.
    }
  }
}

/**
 * Whether the user explicitly declined `purpose` on a surface that loads Ketch.
 * Absence of a decision is not a decline, so this returns false when we've never been told.
 */
export function consentDeclined(purpose: KetchConsentPurposeValue): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    return window.localStorage.getItem(DECLINED_STORAGE_PREFIX + purpose) === '1'
  } catch {
    return false
  }
}

export function showKetchPreferences(): void {
  if (typeof window === 'undefined') {
    return
  }

  if (!window.ketch) {
    console.warn('Ketch is not loaded; cannot open consent preferences')
    return
  }

  try {
    window.ketch('showPreferences')
  } catch (error) {
    console.error('Error opening Ketch preferences:', error)
  }
}

export function onKetchConsent(purpose: KetchConsentPurposeValue, onGrant: () => void, onRevoke?: () => void): void {
  if (typeof window === 'undefined') {
    return
  }

  if (!window.ketch) {
    const checkInterval = setInterval(() => {
      if (window.ketch) {
        clearInterval(checkInterval)
        clearTimeout(timeoutId)
        setupConsentListener()
      }
    }, 100)

    const timeoutId = setTimeout(() => {
      clearInterval(checkInterval)
    }, 5000)
    return
  }

  setupConsentListener()

  function setupConsentListener() {
    let previousConsent: boolean | undefined = undefined

    try {
      window.ketch!('on', 'consent', (consent: KetchConsent | undefined) => {
        // Record declines for every purpose the app cares about, not just `purpose`, so the
        // app inherits the decision no matter which purpose this caller subscribed to.
        persistDeclinedPurposes(consent)

        const hasConsent = consent?.purposes?.[purpose]

        if (hasConsent) {
          onGrant()
        } else if (onRevoke && hasConsent === false && previousConsent === true) {
          onRevoke()
        }

        previousConsent = hasConsent
      })
    } catch (error) {
      console.error('Error setting up Ketch consent listener:', error)
    }
  }
}
